/**
 * CardSearchStep - Search phase of the Add to Collection modal
 *
 * Results are grouped by oracle card with printing counts and owned quantities.
 * The modal keeps this step mounted (hidden) during later phases so the query and results survive.
 */
import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { IoChevronForward } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import Search from '@/components/Search/Search';
import Spinner from '@/components/Spinner/Spinner';
import { useCollectionQuery } from '@/features/collection/hooks/api';
import useCardIndex from '@/hooks/useCardIndex';
import type { MinimalCard } from '@/services/cardDatabase';

// ============================================================================
// Types
// ============================================================================

export interface CardSearchStepProps {
  /** Called when a card is selected from results */
  onSelectCard: (card: MinimalCard) => void;
  /** Whether this is the visible phase; the step stays mounted while hidden */
  isActive: boolean;
  /** Oracle id of the card viewed last — highlighted and scrolled into view on return */
  highlightedOracleId?: string | null;
  /** Optional className for container */
  className?: string;
}

interface GroupedResult {
  /** Representative card (one printing) */
  card: MinimalCard;
  /** Total printings available */
  printingCount: number;
  /** Total copies owned across all printings */
  ownedCount: number;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_SEARCH_RESULTS = 50;
const DEBOUNCE_MS = 500;

// ============================================================================
// Component
// ============================================================================

export default function CardSearchStep({ onSelectCard, isActive, highlightedOracleId, className }: CardSearchStepProps): ReactElement {
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  // Typed text is only needed to detect a pending debounce on Enter, so it lives in a ref
  const typedTextRef = useRef('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const { loading: indexLoading, error: indexError, searchCardsByPrefix, getAllPrintingsByName } = useCardIndex();
  const { data: collection } = useCollectionQuery();

  // Build owned quantities map: oracle_id -> count
  const ownedByOracleId = useMemo(() => {
    const map = new Map<string, number>();
    if (!collection) return map;

    for (const item of collection) {
      // Group by oracle_id for version-agnostic counting
      // EnrichedCollectionCard has card.oracle_id via the joined ScryfallCard
      const oracleId = item.card?.oracle_id;
      if (oracleId) {
        const current = map.get(oracleId) ?? 0;
        map.set(oracleId, current + item.quantity);
      }
    }
    return map;
  }, [collection]);

  // Printing counts are O(index) lookups; cache per name so collection updates don't redo them
  const printingCountCache = useMemo(() => new Map<string, number>(), [getAllPrintingsByName]);

  // Search and group results by oracle card
  const groupedResults = useMemo<GroupedResult[]>(() => {
    const query = debouncedSearchText.trim();
    if (!query) return [];

    // Group by oracle_id so different cards with the same name both appear
    const groupMap = new Map<string, Omit<GroupedResult, 'printingCount'>>();
    for (const card of searchCardsByPrefix(query, MAX_SEARCH_RESULTS * 3)) {
      if (card.oracle_id && !groupMap.has(card.oracle_id)) {
        groupMap.set(card.oracle_id, { card, ownedCount: ownedByOracleId.get(card.oracle_id) ?? 0 });
      }
    }

    // Owned cards first, then alphabetically
    const results = Array.from(groupMap.values()).sort((a, b) => {
      if (a.ownedCount > 0 && b.ownedCount === 0) return -1;
      if (a.ownedCount === 0 && b.ownedCount > 0) return 1;
      return a.card.name.localeCompare(b.card.name);
    });

    return results.slice(0, MAX_SEARCH_RESULTS).map((result) => {
      let printingCount = printingCountCache.get(result.card.name);
      if (printingCount === undefined) {
        printingCount = getAllPrintingsByName(result.card.name).length;
        printingCountCache.set(result.card.name, printingCount);
      }
      return { ...result, printingCount };
    });
  }, [debouncedSearchText, searchCardsByPrefix, getAllPrintingsByName, ownedByOracleId, printingCountCache]);

  const highlightedIndex = highlightedOracleId ? groupedResults.findIndex((r) => r.card.oracle_id === highlightedOracleId) : -1;

  // New results: start keyboard navigation from the last viewed card (if listed)
  useEffect(() => {
    setActiveIndex(highlightedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedResults]);

  // Returning to this phase: refocus the input and bring the last viewed card into view
  useEffect(() => {
    if (!isActive) return;
    inputRef.current?.focus();
    setActiveIndex(highlightedIndex);
    listRef.current?.children[highlightedIndex]?.scrollIntoView({ block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, groupedResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Results still reflect an older query while the debounce is pending
      if (typedTextRef.current.trim() !== debouncedSearchText.trim()) {
        setDebouncedSearchText(typedTextRef.current);
        return;
      }
      const result = groupedResults[activeIndex] ?? groupedResults[0];
      if (result) onSelectCard(result.card);
    }
  };

  const hasQuery = Boolean(debouncedSearchText.trim());
  const activeOptionId = activeIndex >= 0 && activeIndex < groupedResults.length ? `${listId}-option-${activeIndex}` : undefined;

  return (
    <div className={clsx(isActive ? 'flex flex-col' : 'hidden', className)}>
      {/* Search Input */}
      <Search
        ref={inputRef}
        onSearch={setDebouncedSearchText}
        onChangeImmediate={(value) => {
          typedTextRef.current = value;
        }}
        onKeyDown={handleKeyDown}
        debounceMs={DEBOUNCE_MS}
        placeholder="Search cards by name..."
        ariaLabel="Search cards to add to collection"
        ariaControls={listId}
        ariaActiveDescendant={activeOptionId}
        isLoading={indexLoading}
        autoFocus
      />

      {/* Results Area */}
      <div className="mt-4 max-h-150 min-h-75 overflow-y-auto">
        {/* Loading State */}
        {indexLoading && (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <Spinner size="md" />
            <p className="text-ash text-sm">Loading card database...</p>
          </div>
        )}

        {/* Error State */}
        {indexError && <div className="text-danger py-8 text-center text-sm">{indexError}</div>}

        {/* Empty Search State */}
        {!indexLoading && !indexError && !hasQuery && (
          <div className="text-ash flex flex-col items-center justify-center gap-2 py-12 text-center">
            <p className="text-sm">Type a card name to search</p>
            <p className="text-xs opacity-60">Results will show all printings and your owned copies</p>
          </div>
        )}

        {/* No Results State */}
        {!indexLoading && !indexError && hasQuery && groupedResults.length === 0 && (
          <div className="text-ash flex flex-col items-center justify-center gap-2 py-12 text-center">
            <p className="text-sm">No cards found for "{debouncedSearchText}"</p>
            <p className="text-xs opacity-60">Try a different search term</p>
          </div>
        )}

        {/* Results List */}
        {!indexLoading && !indexError && groupedResults.length > 0 && (
          <ul ref={listRef} id={listId} role="listbox" aria-label="Matching cards" className="divide-storm divide-y">
            {groupedResults.map((result, index) => (
              <CardSearchResult
                key={result.card.oracle_id}
                id={`${listId}-option-${index}`}
                result={result}
                isActive={index === activeIndex}
                isLastViewed={index === highlightedIndex}
                onSelect={() => onSelectCard(result.card)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Results Count - Fixed at bottom */}
      {!indexLoading && !indexError && groupedResults.length > 0 && (
        <div className="text-ash border-storm flex items-center justify-between border-t px-4 py-2 text-xs">
          <span>
            Showing {groupedResults.length} result{groupedResults.length !== 1 ? 's' : ''}
            {groupedResults.length >= MAX_SEARCH_RESULTS && ' (limit reached)'}
          </span>
          <span className="opacity-70">↑↓ to move · Enter to select</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CardSearchResult Subcomponent
// ============================================================================

interface CardSearchResultProps {
  id: string;
  result: GroupedResult;
  /** Keyboard-active option */
  isActive: boolean;
  /** The card shown in later phases before returning to search */
  isLastViewed: boolean;
  onSelect: () => void;
}

function CardSearchResult({ id, result, isActive, isLastViewed, onSelect }: CardSearchResultProps): ReactElement {
  const { card, printingCount, ownedCount } = result;

  return (
    <li
      id={id}
      role="option"
      aria-selected={isActive}
      onClick={onSelect}
      className={clsx(
        'group relative flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors',
        isLastViewed ? 'bg-aether/10 hover:bg-aether/15 ring-aether/60 ring-1 ring-inset' : isActive ? 'bg-storm/70' : 'hover:bg-storm/50',
      )}
    >
      {isActive && <span className="bg-aether absolute inset-y-0 left-0 w-0.5" aria-hidden="true" />}

      {/* Card Thumbnail */}
      <CardHoverPreview imageUris={card.image_uris ?? null} name={card.name}>
        <div className="h-14 w-10 shrink-0 overflow-hidden rounded shadow-md">
          {card.image_uris?.small ? (
            <img src={card.image_uris.small} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="bg-slate flex h-full w-full items-center justify-center">
              <span className="text-ash text-xs">?</span>
            </div>
          )}
        </div>
      </CardHoverPreview>

      {/* Card Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="text-pearl text-sm leading-tight font-medium">{card.name}</h4>
          {isLastViewed && <span className="bg-aether/20 text-aether rounded-full px-2 py-0.5 text-[10px] font-medium">Last viewed</span>}
        </div>

        {/* Type Line */}
        <p className="text-ash mt-0.5 truncate text-xs">{card.type_line}</p>

        {/* Metadata Row */}
        <div className="mt-1.5 flex items-center gap-3 text-xs">
          <span className="text-silver">
            {printingCount} {printingCount === 1 ? 'printing' : 'printings'}
          </span>
          {ownedCount > 0 ? (
            <span className="text-aether font-medium">{ownedCount} owned</span>
          ) : (
            <span className="text-ash/60">not owned</span>
          )}
        </div>
      </div>

      <IoChevronForward
        className={clsx(
          'h-5 w-5 shrink-0 transition-all',
          isActive ? 'text-aether' : 'text-ash group-hover:text-silver group-hover:translate-x-0.5',
        )}
        aria-hidden="true"
      />
    </li>
  );
}
